import { gql } from '@apollo/client';
import { Button, FormControl, FormLabel, HStack, SimpleGrid, VStack } from '@chakra-ui/react';
import { countries } from 'countries-list';
import { debounce } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { Controller, ControllerRenderProps, UseFormReturn } from 'react-hook-form';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { useOrgTypeAheadLazyQuery } from './__generated__/organisationDetailsForm.graphql';
import { FormError } from './error';
import { CreateOrgDataType } from './schema';

export type OrganisationDetailsFormProps = {
  form: UseFormReturn<CreateOrgDataType>;
};

const countryOptions = Object.entries(countries)
  .map(([code, country]) => ({
    label: country.name,
    value: code,
  }))
  .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));

const startsWithFilter = (
  option: { label: string; value: string },
  inputValue: string,
): boolean => {
  if (!inputValue) return true;
  return option.label.toLowerCase().startsWith(inputValue.toLowerCase());
};

export function OrganisationDetailsForm({ form }: OrganisationDetailsFormProps) {
  const {
    control,
    formState: { errors },
    watch,
  } = form;

  const countryCode = watch('countryCode');

  const isOraganisationFieldsDisabled = !countryCode;
  const [selectedCountry, setSelectedCountry] = useState<
    { label: string; value: string } | undefined | null
  >(undefined);

  useEffect(() => {
    if (!countryCode) setSelectedCountry(null);
  }, [countryCode, setSelectedCountry]);

  return (
    <VStack w={'100%'} align="stretch" spacing={2} mt={6}>
      <SimpleGrid spacing={4} columns={{ base: 1, md: 2 }}>
        <FormControl isRequired isInvalid={Boolean(errors.countryCode)}>
          <FormLabel>1. Select Country</FormLabel>
          <Controller
            name="countryCode"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                value={selectedCountry}
                options={countryOptions}
                placeholder="Click and select organisation's country"
                filterOption={startsWithFilter}
                formatOptionLabel={(country) => {
                  return country ? (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <ReactCountryFlag
                        countryCode={country.value}
                        svg
                        alt={country?.label}
                        style={{
                          width: 20,
                          marginRight: 16,
                          marginLeft: 10,
                        }}
                      />
                      {country?.label}
                    </div>
                  ) : (
                    ''
                  );
                }}
                onChange={(selectedOption) => {
                  field.onChange(selectedOption?.value || '');
                  setSelectedCountry(selectedOption);
                }}
              />
            )}
          />
          <FormError>{errors.countryCode?.message}</FormError>
        </FormControl>
        <FormControl isRequired isInvalid={Boolean(errors.organisationDetail?.message)}>
          <FormLabel>2. Organisation Name</FormLabel>
          <Controller
            name="organisationDetail"
            control={control}
            render={({ field }) =>
              CreatableSelectableOrgName({
                field,
                form,
                countryCode,
                isDisabled: isOraganisationFieldsDisabled,
                placeholder: 'When country selected,start typing and select organisation',
              })
            }
          />
          <FormError>{errors.organisationDetail?.message}</FormError>
        </FormControl>
      </SimpleGrid>
    </VStack>
  );
}

type CreatableSelectableOrgNameProps = {
  field: ControllerRenderProps<CreateOrgDataType, 'organisationDetail'>;
  form: UseFormReturn<CreateOrgDataType>;
  countryCode: string;
  isDisabled: boolean;
  placeholder: string;
};

function CreatableSelectableOrgName({
  field,
  countryCode,
  isDisabled,
  placeholder,
  form: { setError, watch, setValue },
}: CreatableSelectableOrgNameProps) {
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
  const [orgDetails, setOrgDetails] = useState<
    CreateOrgDataType['organisationDetail'] | null | undefined
  >(undefined);
  const [fetchOrgTypeAhead, { data, loading, error }] = useOrgTypeAheadLazyQuery();

  const debouncedFetchRef = useRef(
    debounce(
      async (countryCode: string, searchTerm: string) =>
        await fetchOrgTypeAhead({ variables: { country: countryCode, searchTerm } }),
      500,
    ),
  );
  const watchedOrgDetails = watch('organisationDetail');

  useEffect(() => {
    if (!watchedOrgDetails) {
      setOrgDetails(null);
      setSearchTerm('');
      setValue('organisationHandle', '');
    } else setOrgDetails(watchedOrgDetails);
  }, [watchedOrgDetails, setValue]);

  useEffect(() => {
    if (error) {
      switch (error.message) {
        case 'BAD_REQUEST':
          setError('organisationDetail', {
            type: 'manual',
            message: 'Invalid organisation name or country.',
          });
          break;
        case 'internal_server_error':
          setError('organisationDetail', {
            type: 'manual',
            message: 'Error while fetching org type ahead values.',
          });
          break;
      }
    }
  }, [data, error, setError]);

  const selectOptions = useMemo(() => {
    if (!data || !searchTerm) return [];

    return data.orgs.map((org) => ({
      orgName: org.name,
      orgAddress: org.address,
      dunsId: org.dunsId || '',
      isCreated: false,
      // react-select throws error if label and value is not provided, so keeping this even though not used
      label: org.name,
      value: org.name,
    }));
  }, [data, searchTerm]);

  useEffect(() => {
    if (!searchTerm || !countryCode) return;
    if (searchTerm === generateSearchTerm(watchedOrgDetails)) return;

    const debouncedFetch = debouncedFetchRef.current;
    debouncedFetch(countryCode, searchTerm);
    return () => {
      debouncedFetch.cancel();
    };
  }, [countryCode, searchTerm, watchedOrgDetails]);

  useEffect(() => {
    setOrgDetails(null);
    setSearchTerm('');
    setValue('organisationHandle', '');
  }, [countryCode, setValue]);

  const handleInputChange = (inputValue: string, { action }: { action: string }) => {
    if (!['input-blur', 'menu-close'].includes(action)) setSearchTerm(inputValue);
  };

  const generateSearchTerm = (option?: CreateOrgDataType['organisationDetail'] | null) =>
    option ? `${option.orgName}${option.orgAddress ? ', ' + option.orgAddress : ''}` : '';

  return (
    <CreatableSelect
      {...field}
      value={orgDetails}
      isClearable
      allowCreateWhileLoading
      isLoading={loading}
      isDisabled={isDisabled}
      inputValue={searchTerm}
      options={selectOptions}
      placeholder={placeholder}
      onInputChange={handleInputChange}
      createOptionPosition={'first'}
      getOptionLabel={(option) => generateSearchTerm(option)}
      getNewOptionData={(inputValue) => ({
        orgName: inputValue,
        orgAddress: '',
        dunsId: '',
        isCreated: true,
        label: inputValue,
        value: inputValue,
      })}
      formatOptionLabel={(option, { context }) => {
        if (option && option.isCreated && context === 'menu') {
          return (
            <HStack width={'100%'} justifyContent={'space-between'}>
              <span>{option.orgName}</span>
              <Button borderRadius={12} size={'xs'} bg={'#BCCCDC'}>
                + Add new
              </Button>
            </HStack>
          );
        }
        return generateSearchTerm(option);
      }}
      controlShouldRenderValue={true}
    />
  );
}

OrganisationDetailsForm.graphql = {
  queries: {
    orgTypeAhead: gql`
      query OrgTypeAhead($country: String!, $searchTerm: String!) {
        orgs: orgTypeAhead(country: $country, searchTerm: $searchTerm) {
          dunsId
          name
          address
        }
      }
    `,
  },
};
